import React, { useEffect, useRef } from 'react';

const WordCloud = ({ data, onClose }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!data || data.length === 0) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // 设置画布大小
    canvas.width = 800;
    canvas.height = 400;
    
    // 清空画布
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // 计算最大和最小频率
    const maxCount = Math.max(...data.map(item => item.count));
    const minCount = Math.min(...data.map(item => item.count));
    
    // 定义颜色数组 - 更丰富的配色
    const colors = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7',
      '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9',
      '#F8C471', '#82E0AA', '#F1948A', '#85C1E9', '#D7BDE2',
      '#A8E6CF', '#FFB3BA', '#FFD93D', '#6BCF7F', '#4D96FF'
    ];
    
    // 存储已放置的词汇位置
    const placedWords = [];
    
    // 为每个词汇计算位置和样式
    data.forEach((item, index) => {
      // 计算字体大小 (18-56px) - 更大的字体范围
      const fontSize = Math.max(18, Math.min(56, 18 + (item.count - minCount) / (maxCount - minCount) * 38));
      
      // 设置字体
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      
      // 测量文本尺寸
      const textMetrics = ctx.measureText(item.word);
      const textWidth = textMetrics.width;
      const textHeight = fontSize;
      
      // 计算位置 (改进的螺旋布局)
      let x, y;
      let attempts = 0;
      const maxAttempts = 200; // 增加尝试次数
      
      do {
        // 改进的螺旋算法 - 更紧凑的布局
        const angle = attempts * 0.3; // 减小角度增量
        const radius = attempts * 1.5; // 减小半径增量
        x = canvas.width / 2 + Math.cos(angle) * radius;
        y = canvas.height / 2 + Math.sin(angle) * radius;
        
        // 确保在画布范围内
        x = Math.max(textWidth / 2, Math.min(canvas.width - textWidth / 2, x));
        y = Math.max(textHeight / 2, Math.min(canvas.height - textHeight / 2, y));
        
        attempts++;
      } while (attempts < maxAttempts && isOverlapping(x, y, textWidth, textHeight, placedWords));
      
      // 记录已放置的词汇
      placedWords.push({
        x: x - textWidth / 2,
        y: y - textHeight / 2,
        width: textWidth,
        height: textHeight
      });
      
      // 设置颜色 - 根据频率调整透明度
      const color = colors[index % colors.length];
      const alpha = 0.7 + (item.count / maxCount) * 0.3; // 频率越高越不透明
      ctx.fillStyle = color;
      ctx.globalAlpha = alpha;
      
      // 绘制文本
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(item.word, x, y);
      
      // 添加阴影效果 - 根据字体大小调整
      ctx.shadowColor = 'rgba(0, 0, 0, 0.15)';
      ctx.shadowBlur = fontSize * 0.1;
      ctx.shadowOffsetX = fontSize * 0.02;
      ctx.shadowOffsetY = fontSize * 0.02;
    });
    
    // 重置阴影和透明度
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.globalAlpha = 1;
    
  }, [data]);
  
  // 检查是否重叠 - 改进的算法
  const isOverlapping = (x, y, width, height, placedWords) => {
    return placedWords.some(word => {
      // 动态间距 - 根据字体大小调整
      const margin = Math.max(3, Math.min(width, height) * 0.1);
      
      // 检查矩形是否重叠
      const rect1 = {
        left: x - width / 2 - margin,
        right: x + width / 2 + margin,
        top: y - height / 2 - margin,
        bottom: y + height / 2 + margin
      };
      
      const rect2 = {
        left: word.x - margin,
        right: word.x + word.width + margin,
        top: word.y - margin,
        bottom: word.y + word.height + margin
      };
      
      return !(rect1.left > rect2.right || 
               rect1.right < rect2.left || 
               rect1.top > rect2.bottom || 
               rect1.bottom < rect2.top);
    });
  };

  if (!data || data.length === 0) {
    return (
      <div className="wordcloud-container">
        <div className="wordcloud-header">
          <h2>弹幕词云统计</h2>
          <button className="btn btn-secondary" onClick={onClose}>关闭</button>
        </div>
        <div className="wordcloud-content">
          <p style={{color: '#666', fontStyle: 'italic', textAlign: 'center'}}>暂无弹幕数据</p>
        </div>
      </div>
    );
  }

  return (
    <div className="wordcloud-container">
      <div className="wordcloud-header">
        <h2>弹幕词云统计</h2>
        <button className="btn btn-secondary" onClick={onClose}>关闭</button>
      </div>
      
      <div className="wordcloud-content">
        <canvas 
          ref={canvasRef}
          className="wordcloud-canvas"
          style={{
            border: '2px solid #e8eaed',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)',
            boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.06)'
          }}
        />
        
        {/* 交互式词汇列表 */}
        <div className="wordcloud-legend">
          <h4>高频词汇排行</h4>
          <div className="word-list">
            {data.slice(0, 10).map((item, index) => (
              <div key={index} className="word-item">
                <span className="word-rank">{index + 1}</span>
                <span className="word-text">{item.word}</span>
                <span className="word-count">{item.count}次</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className="wordcloud-stats">
        <p>统计说明：</p>
        <ul>
          <li>字体大小表示词汇出现频率</li>
          <li>颜色随机分配，便于区分</li>
          <li>共统计了 {data.length} 个高频词汇</li>
          <li>最高频词汇："{data[0]?.word}" ({data[0]?.count} 次)</li>
        </ul>
      </div>

      <style jsx>{`
        .wordcloud-container {
          margin-top: 40px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
          overflow: hidden;
        }

        .wordcloud-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px 24px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }

        .wordcloud-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
        }

        .wordcloud-content {
          display: flex;
          padding: 24px;
          background: #fafbfc;
          gap: 24px;
          align-items: flex-start;
        }

        .wordcloud-canvas {
          flex: 1;
          max-width: 800px;
          height: 400px;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
        }

        .wordcloud-legend {
          flex: 0 0 280px;
          background: white;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          border: 1px solid #f1f5f9;
        }

        .wordcloud-legend h4 {
          margin: 0 0 12px 0;
          color: #333;
          font-size: 14px;
          font-weight: 600;
        }

        .word-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .word-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 8px 12px;
          border-radius: 8px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          transition: all 0.3s ease;
          border: 1px solid transparent;
        }

        .word-item:hover {
          background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
          border-color: #94a3b8;
          transform: translateY(-1px);
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

        .word-rank {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-size: 12px;
          font-weight: bold;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          box-shadow: 0 2px 4px rgba(102, 126, 234, 0.3);
        }

        .word-text {
          flex: 1;
          font-size: 13px;
          color: #333;
          font-weight: 500;
        }

        .word-count {
          font-size: 11px;
          color: #475569;
          background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
          padding: 3px 8px;
          border-radius: 12px;
          font-weight: 600;
          border: 1px solid #cbd5e1;
        }

        .wordcloud-stats {
          padding: 20px 24px;
          background: white;
          border-top: 1px solid #eee;
          font-size: 14px;
          color: #666;
        }

        .wordcloud-stats ul {
          margin: 10px 0;
          padding-left: 20px;
        }

        .wordcloud-stats li {
          margin: 5px 0;
        }

        @media (max-width: 768px) {
          .wordcloud-content {
            flex-direction: column;
          }
          
          .wordcloud-canvas {
            max-width: 100%;
            height: 300px;
          }
          
          .wordcloud-legend {
            flex: none;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default WordCloud; 